import React, { Fragment, useMemo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  formatHourLabel,
  formatPercent,
  formatShortWeekday,
  getPeakTimeColor,
  PEAK_TIME_LEVELS,
  WEEK_DAY_DISPLAY_ORDER,
} from '../../utils/dashboardMetricUtils';
import { BRAND } from '../../theme/brandTokens';

const CELL_HEIGHT = 34;
const CELL_GAP = 6;

const levelRangeLabel = (index) => {
  const min = index === 0 ? 0 : PEAK_TIME_LEVELS[index - 1].maxPercent + 1;
  return `${min}–${PEAK_TIME_LEVELS[index].maxPercent}%`;
};

const PeakTimeBoard = ({ hours, cells }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const cellByKey = useMemo(
    () => new Map(cells.map((cell) => [`${cell.weekDay}-${cell.hour}`, cell])),
    [cells],
  );

  return (
    <Stack spacing={1.5}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          role="grid"
          aria-label={t('dashboard.peak.title')}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto repeat(7, minmax(52px, 1fr))',
            gap: `${CELL_GAP}px`,
            alignItems: 'center',
            minWidth: 300,
          }}
        >
          <Box />
          {WEEK_DAY_DISPLAY_ORDER.map((weekDay) => (
            <Typography key={weekDay} variant="caption" align="center" sx={{ color: BRAND.textMuted, fontWeight: 600 }}>
              {formatShortWeekday(weekDay, locale)}
            </Typography>
          ))}

          {hours.map((hour) => (
            <Fragment key={hour}>
              <Typography
                variant="caption"
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  pr: 1,
                  color: BRAND.textMuted,
                  whiteSpace: 'nowrap',
                  bgcolor: BRAND.surface,
                }}
              >
                {formatHourLabel(hour)}
              </Typography>
              {WEEK_DAY_DISPLAY_ORDER.map((weekDay) => {
                const cell = cellByKey.get(`${weekDay}-${hour}`);
                const percent = cell?.occupancyPercent ?? null;
                const weekdayLabel = formatShortWeekday(weekDay, locale);
                const hourLabel = formatHourLabel(hour);
                const valueLabel = percent === null ? t('dashboard.peak.closed') : formatPercent(percent, locale);
                const description = t('dashboard.peak.cellTooltip', { weekday: weekdayLabel, hour: hourLabel, percent: valueLabel });

                return (
                  <Tooltip key={weekDay} title={description} enterTouchDelay={0} arrow>
                    <Box
                      role="gridcell"
                      aria-label={description}
                      sx={{
                        height: CELL_HEIGHT,
                        borderRadius: 1.5,
                        bgcolor: getPeakTimeColor(percent),
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {percent > 0 ? formatPercent(percent, locale) : null}
                    </Box>
                  </Tooltip>
                );
              })}
            </Fragment>
          ))}
        </Box>
      </Box>

      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" aria-label={t('dashboard.peak.title')}>
        {PEAK_TIME_LEVELS.map((level, index) => (
          <Stack key={level.color} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: level.color }} />
            <Typography variant="caption" sx={{ color: BRAND.textMuted }}>{levelRangeLabel(index)}</Typography>
          </Stack>
        ))}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: BRAND.lineSoft }} />
          <Typography variant="caption" sx={{ color: BRAND.textMuted }}>{t('dashboard.peak.closed')}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PeakTimeBoard;
